/* eslint-disable */
// @ts-nocheck

import { Building2, SlidersHorizontal, Tag, Users } from 'lucide-react'
import { useState } from 'react'

import { getManagers } from '@/api/managersAPI'
import { getProviders, getStorages, getTransporters } from '@/api/providersAPI'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import AsyncSelect from '@/containers/logistic/containers/components/AsyncSelect'
import { useDebounce } from '@/hooks/useDebounce'

interface AdvancedFiltersProps {
  ownerCompanyId: string
  logisticCompanyId: string
  transporterCompanyId: string
  storageCompanyId: string
  category: string[]
  updateOwnerCompanyId: (id: string) => void
  updateLogisticCompanyId: (id: string) => void
  updateTransporterCompanyId: (id: string) => void
  updateStorageCompanyId: (id: string) => void
  updateCategory: (categories: string[]) => void
  categoryOptions: { value: string; label: string }[]
}

export const AdvancedFilters = ({
  ownerCompanyId,
  logisticCompanyId,
  transporterCompanyId,
  storageCompanyId,
  category,
  updateOwnerCompanyId,
  updateLogisticCompanyId,
  updateTransporterCompanyId,
  updateStorageCompanyId,
  updateCategory,
  categoryOptions,
}: AdvancedFiltersProps) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  // Local state for staged filter values
  const [localOwner, setLocalOwner] = useState(
    ownerCompanyId && ownerCompanyId !== '' ? parseInt(ownerCompanyId) : 0
  )
  const [localLogistic, setLocalLogistic] = useState(
    logisticCompanyId && logisticCompanyId !== ''
      ? parseInt(logisticCompanyId)
      : 0
  )
  const [localTransporter, setLocalTransporter] = useState(
    transporterCompanyId && transporterCompanyId !== ''
      ? parseInt(transporterCompanyId)
      : 0
  )
  const [localStorage, setLocalStorage] = useState(
    storageCompanyId && storageCompanyId !== '' ? parseInt(storageCompanyId) : 0
  )
  const [localCategory, setLocalCategory] = useState(
    category && category.length > 0 && !category.includes('all')
      ? category[0]
      : 'all'
  )

  // Estados locales para búsqueda y debounce
  const [ownerSearch, setOwnerSearch] = useState('')
  const [logisticSearch, setLogisticSearch] = useState('')
  const [transporterSearch, setTransporterSearch] = useState('')
  const [storageSearch, setStorageSearch] = useState('')
  const debouncedOwnerSearch = useDebounce(ownerSearch, 500)
  const debouncedLogisticSearch = useDebounce(logisticSearch, 500)
  const debouncedTransporterSearch = useDebounce(transporterSearch, 500)
  const debouncedStorageSearch = useDebounce(storageSearch, 500)

  // Search functions for companies with specific types
  const searchProviderCompanies = async (
    search: string
  ): Promise<{ id: number; label: string }[]> => {
    try {
      const response = await getProviders({ page: 1, per_page: 10, search })
      if (!Array.isArray(response)) return [{ id: 0, label: 'Ninguna' }]

      // Siempre incluir la opción "Ninguna" al principio
      const noneOption = { id: 0, label: 'Ninguna' }

      // Mapear las compañías proveedoras
      const filteredCompanies = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      // Si hay búsqueda y "ninguna" coincide, incluirla
      if (search.toLowerCase().includes('ninguna') || search === '') {
        return [noneOption, ...filteredCompanies]
      }

      return filteredCompanies
    } catch (error) {
      return [{ id: 0, label: 'Ninguna' }]
    }
  }

  const searchLogisticCompanies = async (
    search: string
  ): Promise<{ id: number; label: string }[]> => {
    try {
      const response = await getManagers({ page: 1, per_page: 10, search })
      if (!Array.isArray(response)) return [{ id: 0, label: 'Ninguna' }]

      // Siempre incluir la opción "Ninguna" al principio
      const noneOption = { id: 0, label: 'Ninguna' }

      // Mapear los operadores logísticos
      const filteredCompanies = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      // Si hay búsqueda y "ninguna" coincide, incluirla
      if (search.toLowerCase().includes('ninguna') || search === '') {
        return [noneOption, ...filteredCompanies]
      }

      return filteredCompanies
    } catch (error) {
      return [{ id: 0, label: 'Ninguna' }]
    }
  }

  const searchTransporterCompanies = async (
    search: string
  ): Promise<{ id: number; label: string }[]> => {
    try {
      const response = await getTransporters({ page: 1, per_page: 10, search })
      if (!Array.isArray(response)) return [{ id: 0, label: 'Ninguna' }]

      // Siempre incluir la opción "Ninguna" al principio
      const noneOption = { id: 0, label: 'Ninguna' }

      // Mapear las empresas transportistas
      const filteredCompanies = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      // Si hay búsqueda y "ninguna" coincide, incluirla
      if (search.toLowerCase().includes('ninguna') || search === '') {
        return [noneOption, ...filteredCompanies]
      }

      return filteredCompanies
    } catch (error) {
      return [{ id: 0, label: 'Ninguna' }]
    }
  }

  const searchStorageCompanies = async (
    search: string
  ): Promise<{ id: number; label: string }[]> => {
    try {
      const response = await getStorages({ page: 1, per_page: 10, search })
      if (!Array.isArray(response)) return [{ id: 0, label: 'Ninguna' }]

      // Siempre incluir la opción "Ninguna" al principio
      const noneOption = { id: 0, label: 'Ninguna' }

      // Mapear las empresas de almacenaje
      const filteredCompanies = response.map((company) => ({
        id: company.id,
        label: company.name,
      }))

      // Si hay búsqueda y "ninguna" coincide, incluirla
      if (search.toLowerCase().includes('ninguna') || search === '') {
        return [noneOption, ...filteredCompanies]
      }

      return filteredCompanies
    } catch (error) {
      return [{ id: 0, label: 'Ninguna' }]
    }
  }

  // Sync local state when opening drawer
  const handleOpenChange = (open: boolean) => {
    setIsFilterDrawerOpen(open)
    if (open) {
      setLocalOwner(
        ownerCompanyId && ownerCompanyId !== '' ? parseInt(ownerCompanyId) : 0
      )
      setLocalLogistic(
        logisticCompanyId && logisticCompanyId !== ''
          ? parseInt(logisticCompanyId)
          : 0
      )
      setLocalTransporter(
        transporterCompanyId && transporterCompanyId !== ''
          ? parseInt(transporterCompanyId)
          : 0
      )
      setLocalStorage(
        storageCompanyId && storageCompanyId !== ''
          ? parseInt(storageCompanyId)
          : 0
      )
      setLocalCategory(
        category && category.length > 0 && !category.includes('all')
          ? category[0]
          : 'all'
      )
    }
  }

  const handleApplyFilters = () => {
    updateOwnerCompanyId(localOwner ? localOwner.toString() : '')
    updateLogisticCompanyId(localLogistic ? localLogistic.toString() : '')
    updateTransporterCompanyId(
      localTransporter ? localTransporter.toString() : ''
    )
    updateStorageCompanyId(localStorage ? localStorage.toString() : '')
    updateCategory(localCategory !== 'all' ? [localCategory] : ['all'])
    setIsFilterDrawerOpen(false)
  }

  const handleClearAllFilters = () => {
    updateOwnerCompanyId('')
    updateLogisticCompanyId('')
    updateTransporterCompanyId('')
    updateStorageCompanyId('')
    updateCategory(['all'])
    setIsFilterDrawerOpen(false)
  }

  // Count active advanced filters
  const getActiveAdvancedFiltersCount = () => {
    let count = 0
    if (ownerCompanyId && ownerCompanyId !== '') count++
    if (logisticCompanyId && logisticCompanyId !== '') count++
    if (transporterCompanyId && transporterCompanyId !== '') count++
    if (storageCompanyId && storageCompanyId !== '') count++
    if (category && category.length > 0 && !category.includes('all')) count++
    return count
  }

  return (
    <Sheet onOpenChange={handleOpenChange} open={isFilterDrawerOpen}>
      <SheetTrigger asChild>
        <Button
          aria-label="Filtros avanzados"
          className="gap-1.5"
          variant="outline"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtros avanzados</span>
          {getActiveAdvancedFiltersCount() > 0 && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              {getActiveAdvancedFiltersCount()}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Filtros avanzados</SheetTitle>
          <SheetDescription>
            Configura filtros específicos para encontrar exactamente lo que
            buscas.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Compañía Proveedora */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Compañía Proveedora</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner">Seleccionar compañía proveedora</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedOwnerSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalOwner}
                  onSearch={searchProviderCompanies}
                  placeholder="Seleccione una compañía proveedora"
                  required={false}
                  searchQuery={ownerSearch}
                  setSearchQuery={setOwnerSearch}
                  value={localOwner}
                />
              </div>
            </div>

            <Separator />

            {/* Operador Logístico */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Operador Logístico</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="logistic">Seleccionar operador logístico</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedLogisticSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalLogistic}
                  onSearch={searchLogisticCompanies}
                  placeholder="Seleccione un operador logístico"
                  required={false}
                  searchQuery={logisticSearch}
                  setSearchQuery={setLogisticSearch}
                  value={localLogistic}
                />
              </div>
            </div>

            <Separator />

            {/* Transportista */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Transportista</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="transporter">Seleccionar transportista</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedTransporterSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalTransporter}
                  onSearch={searchTransporterCompanies}
                  placeholder="Seleccione un transportista"
                  required={false}
                  searchQuery={transporterSearch}
                  setSearchQuery={setTransporterSearch}
                  value={localTransporter}
                />
              </div>
            </div>

            <Separator />

            {/* Almacenaje */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Almacenaje</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="storage">Seleccionar almacenaje</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedStorageSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalStorage}
                  onSearch={searchStorageCompanies}
                  placeholder="Seleccione una empresa de almacenaje"
                  required={false}
                  searchQuery={storageSearch}
                  setSearchQuery={setStorageSearch}
                  value={localStorage}
                />
              </div>
            </div>

            <Separator />

            {/* Categorías */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Tag className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Categoría</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Seleccionar categoría</Label>
                <Select onValueChange={setLocalCategory} value={localCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button
            className="flex-1"
            onClick={handleClearAllFilters}
            variant="outline"
          >
            Limpiar
          </Button>
          <Button className="flex-1" onClick={handleApplyFilters}>
            Aplicar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
